/**
 * Spec 200 Fase 8 — one-shot migration from the legacy `cuadrilla`
 * IndexedDB to the split `cuadrilla-data` + `cuadrilla-identity`
 * databases.
 *
 * Why this exists:
 *   The very first version of the web client opened a single
 *   IndexedDB named `cuadrilla`. Two modules then bumped its
 *   version independently — `db.ts` to v1 (works/photos/voice
 *   notes), `keys.ts` to v2 (meta with the WebIdentity row). Those
 *   two open() calls collided in `The requested version (1) is
 *   less than the existing version (2)`. We split the schema across
 *   two physically distinct DBs in the same commit. This file is
 *   the bridge: any browser that already paired before the split
 *   keeps its data through the upgrade, and we never ask the user
 *   to wipe site data.
 *
 * Schema migration policy (project-wide):
 *   - Every IndexedDB schema change bumps the database version and
 *     adds a branch in `onupgradeneeded` that PRESERVES rows.
 *   - When a name change is unavoidable (this file), we ship a
 *     one-shot migration that copies the rows transparently and
 *     drops the legacy database afterward.
 *   - Never break backwards compatibility. Never ask the user to
 *     clear site data. This rule is permanent.
 *
 * Idempotence:
 *   The migration is gated on a `localStorage` sentinel so even a
 *   pathological reload-loop only runs the copy once. If
 *   `localStorage` is unavailable (private mode quirks), the code
 *   falls back to detecting an empty target DB before running.
 */

const LEGACY_DB_NAME = 'cuadrilla';
const LEGACY_DB_LAST_VERSION = 2;

const NEW_DATA_DB_NAME = 'cuadrilla-data';
const NEW_DATA_DB_VERSION = 1;
const NEW_IDENTITY_DB_NAME = 'cuadrilla-identity';
const NEW_IDENTITY_DB_VERSION = 1;

const STORE_WORKS = 'works';
const STORE_PHOTOS = 'photos';
const STORE_VOICE_NOTES = 'voiceNotes';
const STORE_META = 'meta';

const MIGRATION_FLAG = 'cuadrilla-web.idb-split-v1';

// ── Internal helpers ───────────────────────────────────────────────

function readFlag(): boolean {
  try {
    return window.localStorage.getItem(MIGRATION_FLAG) === '1';
  } catch {
    return false;
  }
}

function writeFlag(): void {
  try {
    window.localStorage.setItem(MIGRATION_FLAG, '1');
  } catch {
    // Private mode / quota exceeded — silent. The legacy DB is gone
    // by the time we reach the writeFlag call, so a future open()
    // sees no rows to migrate either way.
  }
}

function openLegacyIfExists(): Promise<IDBDatabase | null> {
  // We "ask" for the version we know the legacy DB last shipped at.
  // If onupgradeneeded fires with `oldVersion === 0`, the DB was
  // created by this open() — meaning the legacy never existed and we
  // should bail without keeping the empty shell around.
  return new Promise((resolve, reject) => {
    let isFresh = false;
    const req = indexedDB.open(LEGACY_DB_NAME, LEGACY_DB_LAST_VERSION);
    req.onupgradeneeded = (event) => {
      if ((event.oldVersion ?? 0) === 0) {
        isFresh = true;
      }
      // Don't create any stores. Either we'll bail (fresh DB) or the
      // existing DB is at this version already (no upgrade needed).
    };
    req.onsuccess = () => {
      if (isFresh) {
        // Brand-new DB created by us — no rows to migrate. Close +
        // delete so we leave no trace.
        const db = req.result;
        db.close();
        const del = indexedDB.deleteDatabase(LEGACY_DB_NAME);
        del.onsuccess = () => resolve(null);
        del.onerror = () => resolve(null);
        del.onblocked = () => resolve(null);
        return;
      }
      resolve(req.result);
    };
    req.onerror = () => reject(req.error ?? new Error('legacy open failed'));
    req.onblocked = () => reject(new Error('legacy DB blocked'));
  });
}

function readAll(db: IDBDatabase, storeName: string): Promise<unknown[]> {
  if (!db.objectStoreNames.contains(storeName)) {
    return Promise.resolve([]);
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result as unknown[]);
    req.onerror = () => reject(req.error ?? new Error('readAll failed'));
  });
}

function openTargetData(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(NEW_DATA_DB_NAME, NEW_DATA_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_WORKS)) {
        db.createObjectStore(STORE_WORKS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_PHOTOS)) {
        const photos = db.createObjectStore(STORE_PHOTOS, { keyPath: 'id' });
        photos.createIndex('byWorkId', 'workId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_VOICE_NOTES)) {
        const voice = db.createObjectStore(STORE_VOICE_NOTES, { keyPath: 'id' });
        voice.createIndex('byWorkId', 'workId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('target data open failed'));
  });
}

function openTargetIdentity(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(NEW_IDENTITY_DB_NAME, NEW_IDENTITY_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'k' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('target identity open failed'));
  });
}

function writeAll(db: IDBDatabase, storeName: string, rows: unknown[]): Promise<void> {
  if (rows.length === 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    for (const row of rows) {
      // `put` upserts by keyPath — safe even if the target DB
      // already had a row with the same id (last writer wins,
      // matches the relay's contract).
      store.put(row);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('writeAll failed'));
    tx.onabort = () => reject(tx.error ?? new Error('writeAll aborted'));
  });
}

function deleteLegacyDb(): Promise<void> {
  return new Promise((resolve) => {
    const del = indexedDB.deleteDatabase(LEGACY_DB_NAME);
    // Don't reject — a "blocked" delete still completes once the
    // open connection closes, and we don't want to fail the boot
    // path over leaving a stale orphan.
    del.onsuccess = () => resolve();
    del.onerror = () => resolve();
    del.onblocked = () => resolve();
  });
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Run the legacy → split migration once per browser. Safe to call
 * on every boot — re-runs are skipped via the `MIGRATION_FLAG`
 * sentinel. Returns the number of rows copied (0 when there was no
 * legacy DB or the migration already ran).
 *
 * Failures here are non-fatal for the rest of the app: if the
 * migration throws, we still let `db.ts` and `keys.ts` open their
 * fresh databases. The legacy DB may stick around as an orphan in
 * that pathological case, but the user's new pair will work.
 */
export async function migrateLegacyDbIfNeeded(): Promise<{
  works: number;
  photos: number;
  voiceNotes: number;
  identity: boolean;
}> {
  const empty = { works: 0, photos: 0, voiceNotes: 0, identity: false };
  if (typeof indexedDB === 'undefined') return empty;
  if (readFlag()) return empty;

  let legacy: IDBDatabase | null = null;
  try {
    legacy = await openLegacyIfExists();
  } catch {
    return empty;
  }
  if (!legacy) {
    // No legacy DB at all → nothing to do. Pin the flag so we don't
    // keep probing on every reload.
    writeFlag();
    return empty;
  }

  try {
    const [works, photos, voiceNotes, metaRows] = await Promise.all([
      readAll(legacy, STORE_WORKS),
      readAll(legacy, STORE_PHOTOS),
      readAll(legacy, STORE_VOICE_NOTES),
      readAll(legacy, STORE_META),
    ]);
    legacy.close();

    if (works.length > 0 || photos.length > 0 || voiceNotes.length > 0) {
      const target = await openTargetData();
      try {
        await Promise.all([
          writeAll(target, STORE_WORKS, works),
          writeAll(target, STORE_PHOTOS, photos),
          writeAll(target, STORE_VOICE_NOTES, voiceNotes),
        ]);
      } finally {
        target.close();
      }
    }

    let identity = false;
    if (metaRows.length > 0) {
      const idDb = await openTargetIdentity();
      try {
        await writeAll(idDb, STORE_META, metaRows);
        identity = true;
      } finally {
        idDb.close();
      }
    }

    await deleteLegacyDb();
    writeFlag();
    return {
      works: works.length,
      photos: photos.length,
      voiceNotes: voiceNotes.length,
      identity,
    };
  } catch (err) {
    // Make sure the connection closes even on partial failure so the
    // legacy DB doesn't stay locked.
    try {
      legacy.close();
    } catch {
      // ignore
    }
    throw err;
  }
}

/** Test-only helper. Resets the migration sentinel so a unit test
 *  can run the migration multiple times against fresh fixtures. */
export function _resetMigrationFlag(): void {
  try {
    window.localStorage.removeItem(MIGRATION_FLAG);
  } catch {
    // private mode etc.
  }
}
